import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import axios from "../../../redux/sagas/axios";
import ModalAlert from "../../components/modal/modal_alert";

function remToPx(rem) {
  const fontSize = getComputedStyle(document.documentElement).fontSize;
  return rem * parseFloat(fontSize);
}

const payjpFormStyle = {
  base: {
    backgroundColor: '#fff',
    fontFamily: "'Noto Sans JP',HiraKakuProN-W3,-apple-system,BlinkMacSystemFont,'Segoe UI','Helvetica Neue',Arial,sans-serif,'Apple Color Emoji','Segoe UI Emoji','Segoe UI Symbol','Noto Color Emoji'",
    fontSize: `${remToPx(1)}px`,
  }
};

const ModalRegisterCreditcard = ({ successFunc, cardEmail }, ref) => {
  const [isAboutSecurityCodeOn, setIsAboutSecurityCodeOn] = useState(false);
  const [isErrorMessageOn, setIsErrorMessageOn] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const payjp = useRef(null);
  const payjpCardElement = useRef(null);
  const cardForm = useRef(null);
  const cardUsernameElement = useRef(null);
  const cardTokenElement = useRef(null);
  const successModalRef = useRef(null);
  const failedModalRef = useRef(null);
  const modalToggleRef = useRef(null);
  const [errorMessages, setErrorMessages] = useState([]);
  const [formAction, setFormAction] = useState();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const refirectPath = `${location.pathname}?${searchParams}`;
    const action = `/creditcards/redirect_tds?r=${encodeURIComponent(`/users/payjp_register?redirect_url=${encodeURIComponent(refirectPath)}`)}`;
    setFormAction(action);
  }, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://js.pay.jp/v2/pay.js";
    document.body.appendChild(script);

    const onLoadScript = () => {
      if (!window.Payjp) {
        setTimeout(onLoadScript, 1);
        return;
      }

      axios.get("/api/creditcard/public").then((res) => {
        payjp.current = Payjp(res.data.publicKey, { threeDSecureWorkflow: 'redirect' });

        const elements = payjp.current.elements();

        const cardNumberElementOptions = {
          placeholder: '例）4980424242424242',
          style: payjpFormStyle,
        };
        const cardNumberElement = elements.create('cardNumber', cardNumberElementOptions);
        cardNumberElement.mount('#card_number');

        const cardExpiryElementOptions = { style: payjpFormStyle };
        const cardExpiryElement = elements.create('cardExpiry', cardExpiryElementOptions);
        cardExpiryElement.mount('#card_expiry');

        const cardCvcElementOptions = {
          placeholder: '例）123',
          style: payjpFormStyle,
        };
        const cardCvcElement = elements.create('cardCvc', cardCvcElementOptions);
        cardCvcElement.mount('#card_cvc');

        payjpCardElement.current = cardNumberElement;
      });
    };
    onLoadScript();

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const SubmitFunc = useCallback(
    (e) => {
      e.preventDefault();

      if (isSubmittingRef.current) {
        return;
      }
      isSubmittingRef.current = true;
      setErrorMessages([]);

      const errorMessages = [];
      const cardUsername = cardUsernameElement.current.value || '';
      if (!cardUsername.trim()) {
        errorMessages.push('契約者名を入力してください。');
      }

      const card = { name: cardUsername, email: cardEmail };

      setIsSubmitting(true);
      payjp.current.createToken(payjpCardElement.current, { three_d_secure: true, card }).then((response) => {
        if (response.error) {
          setIsErrorMessageOn(true);
        } else {
          const token = response.id;
          cardTokenElement.current.value = token;
          cardForm.current.submit();
        }
      }).finally(() => {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      });
    },
    []
  );

  useImperativeHandle(ref, () => ({
    openModal: () => {
      setIsSubmitting(false);
      modalToggleRef.current.click();
    },
  }));

  const yearsList = React.useMemo(() => {
    let years = [];
    for (let i = 0; i < 10; i++) {
      years.push(new Date().getFullYear() + i);
    }

    return years.map((year) => (
      <option value={year} key={year}>
        {year}
      </option>
    ));
  }, []);

  return (
    <React.Fragment>
      {/* Button trigger modal */}
      <div
        data-toggle="modal"
        data-target="#creditcardRegisterModal"
        ref={modalToggleRef}
      />

      {/* Modal */}
      <div
        className="modal fade"
        id="creditcardRegisterModal"
        tabIndex="-1"
        role="dialog"
        aria-labelledby="exampleModalLongTitle"
        aria-hidden="true"
      >
        <div
          style={{ maxWidth: "none", margin: "0.5rem" }}
          className="modal-dialog"
          role="document"
        >
          <div className="modal-content">
            <div className="modal-header">
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body px-0 bg-gray">
              <form method="post" ref={cardForm} action={formAction}>
                <input ref={cardTokenElement} type="hidden" name="token" value="" />
                <div className="container col-md-8 col-lg-6">
                  {isErrorMessageOn && (
                    <div className="alert alert-danger">
                      カード情報が正しくありません。再度正しくご入力ください。
                    </div>
                  )}
                  <div className="container col-md-8 col-lg-6">
                    <p className="h5--18px text-center mt-3 mb-3">
                      クレジットカードの登録
                    </p>
                  </div>
                  {errorMessages.length > 0 && (
                    <div
                      className="alert alert-danger mt20 mb20"
                      id="error-message"
                      role="alert"
                    >
                      <div className="notice" id="messageTxt" dangerouslySetInnerHTML={{ __html: errorMessages.join('<br />') }} />
                    </div>
                  )}
                  <span className="payment-errors" />
                  <div className="mt-3">
                    <label htmlFor="card_number">クレジットカード番号</label>{" "}
                    <span
                      className="label label-warning"
                      style={{ fontSize: "0.5em" }}
                    >
                      必須
                    </span>
                    <br />
                    <div id="card_number" className="form-control number mb-0 rounded-0 border-0 pt-4_5 pb-4_5" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
                  </div>
                  <div className="mt-3">
                    <label htmlFor="card_username">契約者名</label>{" "}
                    <span
                      className="label label-warning"
                      style={{ fontSize: "0.5em" }}
                    >
                      必須
                    </span>
                    <br />
                    <div id="card_username" className="form-control mb-0 rounded-0 border-0 pt-4_5 pb-4_5" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <input
                        ref={cardUsernameElement}
                        id="card_username"
                        name="card_username"
                        placeholder="例）TARO SUZUKI"
                        type="text"
                        style={{all: 'unset', width: '100%', backgroundColor: '#fff', fontSize: '1rem'}}
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <label htmlFor="security_code">セキュリティコード</label>{" "}
                    <span
                      className="label label-warning"
                      style={{ fontSize: "0.5em" }}
                    >
                      必須
                    </span>{" "}
                    <a
                      className="is-aboutcode"
                      onClick={() => setIsAboutSecurityCodeOn((v) => !v)}
                    >
                      セキュリティコードとは？
                    </a>
                    <br />
                    {isAboutSecurityCodeOn && (
                      <p
                        className="payment-aboutcode"
                        style={{ display: "none" }}
                      >
                        セキュリティコードとは
                        <br />
                        クレジットカード番号とは別にカード裏面に印字されている 3
                        桁の数字です。
                        <br />
                        カード裏面の署名欄に記載された数字の末尾 3
                        桁がセキュリティコードです。
                        <br />
                        ※クレジットカード会社によっては記載がない場合があります。
                        <br />
                        ※ßセキュリティコードはクレジットカードの暗証番号ではありませんのでご注意ください。
                      </p>
                    )}
                    <div id="card_cvc" className="form-control mb-0 rounded-0 border-0 pt-4_5 pb-4_5" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
                  </div>
                  <div className="mt-3">
                    <label htmlFor="exp_month">有効期限</label>{" "}
                    <span
                      className="label label-warning"
                      style={{ fontSize: "0.5em" }}
                    >
                      必須
                    </span>
                    <br />
                    <div id="card_expiry" className="form-control mb-0 rounded-0 border-0 pt-4_5 pb-4_5" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
                  </div>
                  <div className="mt-5 mb-3">
                    <button
                      type="button"
                      id="submit-btn"
                      className="a-button a-button__top a-button--orange mx-auto mb-1 text-fw-bold w-100"
                      disabled={isSubmitting}
                      onClick={SubmitFunc}
                    >
                      登録する
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* success modal */}
      <ModalAlert
        ref={successModalRef}
        title="登録完了"
        content="クレジットカード登録に成功しました。"
        approveFunc={() => {
          successFunc();
          modalToggleRef.current.click();
        }}
      />
      <ModalAlert
        ref={failedModalRef}
        title="登録失敗"
        content="クレジットカード登録に失敗しました。"
        approveFunc={() => {}}
      />
    </React.Fragment>
  );
};

export default forwardRef(ModalRegisterCreditcard);
